/* istanbul ignore file */
import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    InputSignal,
    Signal,
    ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { AlertComponent } from '@components/atoms/alert/alert.component';
import { MedpaceDatepickerComponent } from '@components/atoms/medpace-datepicker/medpace-datepicker.component';
import { MedpaceTextareaAutocompleteComponent } from '@components/atoms/textarea-autocomplete/medpace-textarea-autocomplete.component';
import { CardMode } from '@components/card/card.component';
import { SingleTrainTripFormGroup } from '@components/request/travel-request-card/form-groups/detail-form-groups/travel/train-request.formgroup';
import {
    MdsAutocompleteComponent,
    MdsButtonToggle,
    MdsButtonToggleGroupComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { MedpaceAutocompleteObject } from '@models/medpaceAutocompleteObject';
import { TrainTravelType } from '@models/request';
import { TravelViewModelService } from '@services/travel-view-model/travel-view-model.service';
import { PersistentFormControl } from '@utility/persistent-forms';
import { getUniqueObjects, getUniqueStrings } from '@utility/utility';
import { filter, Observable, startWith, switchMap, tap } from 'rxjs';

@Component({
    selector: 'medpace-train-details',
    templateUrl: './train-details.component.html',
    styleUrls: ['./train-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        MdsButtonToggleGroupComponent,
        dateFormatPipe,
        MedpaceTextareaAutocompleteComponent,
        MedpaceDatepickerComponent,
        MdsAutocompleteComponent,
        AlertComponent,
    ],
})
export class TrainDetailsComponent {
    private readonly travelViewModelService = inject(TravelViewModelService);

    private readonly needsTrainTravelControl: Signal<FormControl<boolean | null>> = computed(
        () => this.travelViewModelService.travelFormGroup().controls.travelDetails.controls.train.controls.needsTravel
    );

    public readonly mode: InputSignal<CardMode> = input.required();
    public readonly noTravelOption: InputSignal<boolean> = input(true);
    public readonly formGroup: InputSignal<SingleTrainTripFormGroup> = input.required();

    public readonly TrainTravelType = TrainTravelType;

    public readonly trainOptionButtons: Signal<MdsButtonToggle[]> = computed(() => {
        const buttons: MdsButtonToggle[] = [];

        const noTrainOption: MdsButtonToggle = {
            id: 'noTravel_Radio',
            value: TrainTravelType.NoTravel,
            name: 'No travel',
        };
        buttons.push(
            ...[
                {
                    id: 'roundTrip_Radio',
                    value: TrainTravelType.RoundTrip,
                    name: 'Roundtrip',
                },
                {
                    id: 'oneWay_Radio',
                    value: TrainTravelType.OneWay,
                    name: 'One way',
                },
            ]
        );

        if (this.noTravelOption()) buttons.push(noTrainOption);

        return buttons;
    });

    public readonly autocompleteOptions: Signal<{
        stationOrigin: string[];
        stationDestination: string[];
        specialRequests: MedpaceAutocompleteObject[];
    }> = computed(() => {
        const value = this.travelViewModelService.travelCardViewModel()?.historyData.train;

        return {
            stationOrigin: getUniqueStrings(
                value?.filter((data) => !!data.stationOrigin).map((data) => data.stationOrigin) ?? []
            ),
            stationDestination: getUniqueStrings(
                value?.filter((data) => !!data.stationDestination).map((data) => data.stationDestination) ?? []
            ),
            specialRequests: getUniqueObjects(
                value
                    ?.filter((data) => !!data.specialRequest)
                    .map(
                        (data) =>
                            <MedpaceAutocompleteObject>{ requestDate: data.requestDate, value: data.specialRequest }
                    ) ?? []
            ),
        };
    });

    public readonly travelTypeChange$: Observable<TrainTravelType | null> = toObservable(this.formGroup).pipe(
        filter(Boolean),
        switchMap((formGroup) =>
            formGroup.controls.travelType.valueChanges.pipe(startWith(formGroup.getRawValue().travelType))
        ),
        tap((travelType) => {
            let controlsToEnable: PersistentFormControl[] = [];
            let controlsToDisable: PersistentFormControl[] = [];
            if (travelType === TrainTravelType.RoundTrip) {
                controlsToEnable = Object.values(this.formGroup().controls).filter(
                    (control) => control !== this.formGroup().controls.travelType
                );
                controlsToDisable = [];
            } else if (travelType === TrainTravelType.OneWay) {
                controlsToEnable = Object.values(this.formGroup().controls).filter(
                    (control) =>
                        control !== this.formGroup().controls.travelType &&
                        control !== this.formGroup().controls.returnDate
                );
                controlsToDisable = [this.formGroup().controls.returnDate];
            } else {
                // No_Travel
                controlsToEnable = Object.values(this.formGroup().controls).filter(
                    (control) => control === this.formGroup().controls.travelType
                );
                controlsToDisable = Object.values(this.formGroup().controls).filter(
                    (control) => control !== this.formGroup().controls.travelType
                );
            }
            controlsToEnable.forEach((control) =>
                control.enable({
                    breakPersistence: true,
                    onlySelf: true,
                    emitEvent: false,
                })
            );
            controlsToDisable.forEach((control) =>
                control.disable({
                    persistent: true,
                    onlySelf: true,
                    emitEvent: false,
                })
            );
        })
    );

    private readonly disableReturnDate = effect(() => {
        const formGroup = this.formGroup();
        if (formGroup.controls.travelType.value === TrainTravelType.OneWay) {
            formGroup.controls.returnDate.disable({ emitEvent: false, persistent: true });
        }
    });
}
